import {DestroyRef, inject, Injectable, signal} from "@angular/core"
import {asyncScheduler, Subject, throttleTime} from "rxjs"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {scheduleBackgroundTask, TiledImageManager} from "@cm/browser-utils"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {ThreeSceneManagerService} from "@template-editor/services/three-scene-manager.service"
import {ThreeMaterialManagerService} from "@template-editor/services/three-material-manager.service"
import {CM_LOGGER_SCENE} from "@common/services/logging"
import type {CmLogger} from "@cm/log"
import {Three as THREE} from "@cm/material-nodes/three"
import type {ImageTilesDescriptor} from "@cm/utils"
import {
    ExplicitTextureStreamingMode,
    setThreeTextureStreamingSceneLogger,
    TextureTileId,
    ThreeTextureTilesManager,
    ThreeTiledTextureCallback,
    ThreeTiledTextureInstance,
    ThreeViewportTextureTileUpdater,
    ThreeViewportTextureTileUpdaterMaterialCache,
    USE_DEBUG_TILE_IMAGE,
} from "@cm/three-texture-streaming"
import {createDebugTileImage, createThreeTiledTexture, OffscreenCanvasWebgl2RgbaStaging, type TileKinds} from "@cm/three-texture-streaming-browser"
import {TextureTileLoaderService} from "@common/services/texture-tile-loader/texture-tile-loader.service"

@Injectable()
export class ThreeTextureTilesManagerService {
    readonly $numTilesLoading = signal(0)
    readonly manager: ThreeTextureTilesManager<TileKinds>

    protected readonly permission = inject(PermissionsService)
    protected $can = this.permission.$to

    private readonly logger: CmLogger = inject(CM_LOGGER_SCENE)
    private readonly _destroyRef = inject(DestroyRef)
    private readonly _tiledImageManager = new TiledImageManager(this.logger)

    constructor() {
        setThreeTextureStreamingSceneLogger(this.logger)
        const textureTileLoader = inject(TextureTileLoaderService)
        this.manager = new ThreeTextureTilesManager(
            {
                createTiledTexture: (cb: ThreeTiledTextureCallback, id: number, desc: ImageTilesDescriptor, isSrgb: boolean) =>
                    createThreeTiledTexture({...cb, getTiledImageManager: () => this._tiledImageManager}, id, desc, isSrgb),
                getRenderer: () => this._threeSceneManagerService?.$renderer() ?? null,
                getRendererAsync: () => this._threeSceneManagerService!.getRenderer(),
                getMeshMaterialInfo: (mesh: THREE.Mesh) => {
                    const mat = mesh.material
                    if (Array.isArray(mat)) return null
                    const materialKey = this._threeMaterialManagerService?.getKeyByThreeMaterial(mat)
                    if (!materialKey) return null
                    return this._threeMaterialManagerService?.getInfoForMaterial(materialKey) ?? null
                },
                onTileUpdated: (id) => this.$tileUpdated.next(id),
                onLoadingCountChanged: (count) => this.$numTilesLoading.set(count),
                scheduleTask: scheduleBackgroundTask,
                createWebgl2RgbaStaging: (w, h) => new OffscreenCanvasWebgl2RgbaStaging(w, h),
                createDebugTileImages: USE_DEBUG_TILE_IMAGE
                    ? (fine, coarser, mipLevel, tileIndex) => {
                          const bitmapToPixels = (bitmap: ImageBitmap): Uint8Array<ArrayBuffer> => {
                              const canvas = new OffscreenCanvas(bitmap.width, bitmap.height)
                              const ctx = canvas.getContext("2d")!
                              ctx.drawImage(bitmap, 0, 0)
                              return new Uint8Array(ctx.getImageData(0, 0, bitmap.width, bitmap.height).data.buffer as ArrayBuffer)
                          }
                          const debugFine = createDebugTileImage(fine.width, fine.height, mipLevel, tileIndex)
                          const resultFine = {pixels: bitmapToPixels(debugFine), width: debugFine.width, height: debugFine.height}
                          debugFine.close()
                          let resultCoarser = null
                          if (coarser) {
                              const debugCoarseImg = createDebugTileImage(coarser.width, coarser.height, mipLevel + 1, {
                                  x: tileIndex.x >> 1,
                                  y: tileIndex.y >> 1,
                              })
                              resultCoarser = {pixels: bitmapToPixels(debugCoarseImg), width: debugCoarseImg.width, height: debugCoarseImg.height}
                              debugCoarseImg.close()
                          }
                          return {fine: resultFine, coarser: resultCoarser}
                      }
                    : undefined,
            },
            textureTileLoader,
            this.logger,
        )
        this._destroyRef.onDestroy(() => this.manager.dispose())

        this.$tileUpdated
            .pipe(takeUntilDestroyed(this._destroyRef), throttleTime(500, asyncScheduler, {leading: false, trailing: true}))
            .subscribe(() => this.threeSceneManagerService.redraw({strategy: "integrate"}))
    }

    set threeSceneManagerService(threeSceneManagerService: ThreeSceneManagerService) {
        this._threeSceneManagerService = threeSceneManagerService
    }

    get threeSceneManagerService(): ThreeSceneManagerService {
        if (!this._threeSceneManagerService) {
            throw new Error("ThreeTextureTilesManagerService: threeSceneManagerService not set")
        }
        return this._threeSceneManagerService
    }

    set threeMaterialManagerService(threeMaterialManagerService: ThreeMaterialManagerService) {
        this._threeMaterialManagerService = threeMaterialManagerService
    }

    get tileUpdaterMaterialCache(): ThreeViewportTextureTileUpdaterMaterialCache {
        return this.manager.tileUpdaterMaterialCache
    }

    async createViewportTextureTileUpdater(): Promise<ThreeViewportTextureTileUpdater> {
        return this.manager.createViewportTextureTileUpdater()
    }

    async createTiledTexture(
        imageTilesDescriptor: ImageTilesDescriptor,
        isSrgb: boolean,
        uvNode: THREE.Node<"vec2"> | THREE.Node<"vec3"> | undefined,
        maxTextureResolution?: number,
    ): Promise<ThreeTiledTextureInstance> {
        return this.manager.createTiledTexture(imageTilesDescriptor, isSrgb, uvNode, maxTextureResolution)
    }

    setTextureFiltering(linear: boolean): void {
        this.manager.setTextureFiltering(linear)
    }

    setStreamingMode(streamingMode: ExplicitTextureStreamingMode): void {
        this.manager.setStreamingMode(streamingMode)
    }

    get streamingMode(): ExplicitTextureStreamingMode {
        return this.manager.streamingMode
    }

    private _threeSceneManagerService: ThreeSceneManagerService | null = null // not injected to avoid circular dependency
    private _threeMaterialManagerService: ThreeMaterialManagerService | null = null // not injected to avoid circular dependency
    private readonly $tileUpdated = new Subject<TextureTileId>()
}
